import React, { useMemo, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  ArrowUpward as AscendingIcon,
  ArrowDownward as DescendingIcon,
} from '@mui/icons-material';
import { TimelineEvent } from '../../types/timeline';
import { EventCard } from './EventCard';

interface ChronologicalViewProps {
  events: TimelineEvent[];
  onEventClick?: (event: TimelineEvent) => void;
  loading?: boolean;
  sortOrder?: 'asc' | 'desc';
  onSortOrderChange?: (order: 'asc' | 'desc') => void;
}

// Item height for virtualization (approximate card height)
const ITEM_HEIGHT = 220;
const ITEM_SPACING = 16;

/**
 * ChronologicalView Component
 * Displays timeline events in chronological order with virtualization
 * Part of Phase 3.3: Interactive Timeline Page
 */
export const ChronologicalView: React.FC<ChronologicalViewProps> = ({
  events,
  onEventClick,
  loading = false,
  sortOrder = 'asc',
  onSortOrderChange,
}) => {
  // Sort events by date
  const sortedEvents = useMemo(() => {
    if (!events || events.length === 0) return [];

    const sorted = [...events].sort((a, b) => {
      // Handle events without dates - push to end
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;

      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();

      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return sorted;
  }, [events, sortOrder]);

  // Handle sort order toggle
  const handleSortOrderChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newOrder: 'asc' | 'desc' | null) => {
      if (newOrder && onSortOrderChange) {
        onSortOrderChange(newOrder);
      }
    },
    [onSortOrderChange]
  );

  // Render individual row in virtualized list
  const renderRow = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const event = sortedEvents[index];

      return (
        <div
          style={{
            ...style,
            paddingBottom: ITEM_SPACING,
          }}
        >
          <EventCard event={event} onClick={onEventClick} />
        </div>
      );
    },
    [sortedEvents, onEventClick]
  );

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading timeline events...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Empty state
  if (!sortedEvents || sortedEvents.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Events to Display
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters to see more events.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sort Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {sortedEvents.length} Event{sortedEvents.length !== 1 ? 's' : ''}
        </Typography>

        {onSortOrderChange && (
          <ToggleButtonGroup
            value={sortOrder}
            exclusive
            onChange={handleSortOrderChange}
            size="small"
          >
            <ToggleButton value="asc" aria-label="Sort ascending">
              <AscendingIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">Oldest First</Typography>
            </ToggleButton>
            <ToggleButton value="desc" aria-label="Sort descending">
              <DescendingIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">Newest First</Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {/* Virtualized Event List */}
      <Box sx={{ flex: 1, minHeight: 400 }}>
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <List
              height={height}
              itemCount={sortedEvents.length}
              itemSize={ITEM_HEIGHT}
              width={width}
              overscanCount={5}
            >
              {renderRow}
            </List>
          )}
        </AutoSizer>
      </Box>

      {/* Event Count Footer */}
      <Box
        sx={{
          mt: 2,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Showing {sortedEvents.length} event{sortedEvents.length !== 1 ? 's' : ''} in{' '}
          {sortOrder === 'asc' ? 'chronological' : 'reverse chronological'} order
        </Typography>
      </Box>
    </Box>
  );
};

export default ChronologicalView;
